import { create } from 'zustand'
import { mutate } from 'swr'
import { toast } from '@/components/ui/use-toast'
import { optimisticAddComponents } from 'api'
import { postFetcher, postFormWithProgress } from 'api/swr'
import {
  preparePsdAssets,
  releaseCanvas,
} from 'Editor/PsdImport/preparePsdAssets'
import { previewFromCanvas } from 'Editor/PsdImport/parsePsd'
import {
  dataUrlToBlob,
  ensureWebpDataUrl,
  padImageDataUrl,
  type CanvasPad,
} from './toWebpDataUrl'
import { loadPersistedJobs, persistJobs } from './uploadPersist'

/** 文件进后端出图时的并发。再高 Cloud Run 内存不够。 */
export const MAX_UPLOAD_CONCURRENT = 3

/** 同时画 2048 原图的数量。编码完会马上释放画布。 */
const DIRECT_ENCODE_CONCURRENT = 8

/** 已经压好、还在等上传空位的任务。不再提前把整批原图都编码进内存。 */
const MAX_ENCODED_WAITING = 2

/** 图已经压成 WebP 之后的直传数量。 */
const DIRECT_UPLOAD_CONCURRENT = 8

export type CptUploadStatus =
  | 'queued'
  | 'padding'
  | 'compressing'
  | 'uploading'
  | 'processing'
  | 'error'

export type LayerPlace = {
  left: number
  top: number
  canvas: number
  /** 原画布高，缺省等于 canvas。3:4 PSD 为 2048 */
  canvasHeight?: number
  /** 方图上采纳的范围。竖屏 PSD 用来留出左右空白。 */
  clip?: { left: number; top: number; right: number; bottom: number }
}

export type CptUploadJob = {
  id: string
  tid: string
  lid: string
  dlc: string
  fileName: string
  preview: string
  original: string
  trim: boolean
  ai: boolean
  autoCover: number
  canvasPad?: CanvasPad
  layerPlace?: LayerPlace
  /** PSD：浏览器出图后直传 GCS */
  directGcs?: boolean
  baseSize?: number
  /** 组件逻辑画布高，缺省等于 baseSize。3:4 为 1024 */
  baseHeight?: number
  /** PSD 图层画布。不进本地数据库。 */
  layerCanvas?: HTMLCanvasElement
  /** 上传时再解码这一层。不进本地数据库。 */
  decodeLayer?: () => HTMLCanvasElement
  status: CptUploadStatus
  progress: number
  error?: string
}

export type EnqueueCptUpload = {
  id: string
  tid: string
  lid: string
  dlc: string
  fileName: string
  original: string
  trim: boolean
  ai: boolean
  autoCover: number
  preview?: string
  canvasPad?: CanvasPad
  layerPlace?: LayerPlace
  directGcs?: boolean
  baseSize?: number
  /** 组件逻辑画布高，缺省等于 baseSize。3:4 为 1024 */
  baseHeight?: number
  layerCanvas?: HTMLCanvasElement
  decodeLayer?: () => HTMLCanvasElement
}

export const INTERRUPTED_ERROR = '上传中断，请重试'

const isActiveStatus = (status: CptUploadStatus) => status !== 'error'

const hasActiveJobs = (jobs: CptUploadJob[]) =>
  jobs.some((job) => isActiveStatus(job.status))

type CptUploadState = {
  jobs: CptUploadJob[]
  waveTotal: number
  waveCompleted: number
  enqueue: (items: EnqueueCptUpload[]) => Promise<void>
  claimNextQueued: (match?: (job: CptUploadJob) => boolean) => CptUploadJob | null
  patchJob: (id: string, patch: Partial<CptUploadJob>) => void
  removeJob: (id: string) => void
  completeJob: (id: string) => void
  retryJob: (id: string) => void
  retryFailedJobs: (filter?: { tid?: string; lid?: string }) => number
}

const flushPersist = () => {
  const jobs = useCptUploadStore.getState().jobs.map((job) => {
    const copy = { ...job }
    delete copy.layerCanvas
    delete copy.decodeLayer
    if (copy.original.startsWith('data:')) copy.original = ''
    if (copy.preview.startsWith('data:')) copy.preview = ''
    return copy
  })
  void persistJobs(jobs)
}

export const jobMatchesLayer = (job: { lid?: string }, lid?: string) => {
  if (lid && lid !== 'draft') return job.lid === lid
  return job.lid === 'draft' || !job.lid
}

export const useCptUploadStore = create<CptUploadState>((set, get) => ({
  jobs: [],
  waveTotal: 0,
  waveCompleted: 0,
  enqueue: (items) => {
    if (items.length === 0) return Promise.resolve()
    const jobs: CptUploadJob[] = items.map((item) => ({
      id: item.id,
      tid: item.tid,
      lid: item.lid,
      dlc: item.dlc,
      fileName: item.fileName,
      preview: item.preview ?? item.original,
      original: item.original,
      trim: item.trim,
      ai: item.ai,
      autoCover: item.autoCover,
      canvasPad: item.canvasPad,
      layerPlace: item.layerPlace,
      directGcs: item.directGcs,
      baseSize: item.baseSize,
      baseHeight: item.baseHeight,
      layerCanvas: item.layerCanvas,
      decodeLayer: item.decodeLayer,
      status: 'queued',
      progress: 0,
    }))
    const active = hasActiveJobs(get().jobs)
    set({
      jobs: [...get().jobs, ...jobs],
      waveTotal: active ? get().waveTotal + items.length : items.length,
      waveCompleted: active ? get().waveCompleted : 0,
    })
    tickUploads()
    flushPersist()
    return Promise.resolve()
  },
  claimNextQueued: (match) => {
    const job = get().jobs.find(
      (item) => item.status === 'queued' && (!match || match(item)),
    )
    if (!job) return null
    const next = {
      ...job,
      status: (job.canvasPad ? 'padding' : 'compressing') as CptUploadStatus,
      progress: job.canvasPad ? 8 : 12,
    }
    set({
      jobs: get().jobs.map((item) => (item.id === job.id ? next : item)),
    })
    flushPersist()
    return next
  },
  patchJob: (id, patch) => {
    set({
      jobs: get().jobs.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    })
    if (patch.status || patch.error) flushPersist()
  },
  removeJob: (id) => {
    releaseCanvas(get().jobs.find((item) => item.id === id)?.layerCanvas)
    const jobs = get().jobs.filter((item) => item.id !== id)
    set({
      jobs,
      ...(hasActiveJobs(jobs)
        ? {}
        : { waveTotal: 0, waveCompleted: 0 }),
    })
    flushPersist()
  },
  completeJob: (id) => {
    releaseCanvas(get().jobs.find((item) => item.id === id)?.layerCanvas)
    const jobs = get().jobs.filter((item) => item.id !== id)
    const waveCompleted = get().waveCompleted + 1
    set({
      jobs,
      waveCompleted,
      ...(hasActiveJobs(jobs) ? {} : { waveTotal: 0, waveCompleted: 0 }),
    })
    flushPersist()
  },
  retryJob: (id) => {
    const active = hasActiveJobs(get().jobs)
    set({
      jobs: get().jobs.map((item) =>
        item.id === id
          ? { ...item, status: 'queued', progress: 0, error: undefined }
          : item,
      ),
      waveTotal: active ? get().waveTotal + 1 : 1,
      waveCompleted: active ? get().waveCompleted : 0,
    })
    flushPersist()
    tickUploads()
  },
  retryFailedJobs: (filter) => {
    const tid = filter?.tid
    const lid = filter?.lid
    const active = hasActiveJobs(get().jobs)
    let count = 0
    set({
      jobs: get().jobs.map((item) => {
        if (item.status !== 'error') return item
        if (tid && item.tid !== tid) return item
        if (lid && !jobMatchesLayer(item, lid)) return item
        count += 1
        return { ...item, status: 'queued', progress: 0, error: undefined }
      }),
    })
    if (count > 0) {
      set({
        waveTotal: active ? get().waveTotal + count : count,
        waveCompleted: active ? get().waveCompleted : 0,
      })
      flushPersist()
      tickUploads()
    }
    return count
  },
}))

let bootPromise: Promise<void> | null = null

export const bootCptUploads = () => {
  if (!bootPromise) {
    bootPromise = (async () => {
      const stored = await loadPersistedJobs()
      if (stored.length === 0) return
      const recovered = stored.map((job) =>
        job.status === 'error'
          ? job
          : {
              ...job,
              status: 'error' as const,
              progress: 0,
              error: INTERRUPTED_ERROR,
            },
      )
      useCptUploadStore.setState({ jobs: recovered })
      await persistJobs(recovered)
    })()
  }
  return bootPromise
}

void bootCptUploads()

let serverInFlight = 0
let encodeInFlight = 0
let directUploadInFlight = 0
let ticking = false
const pendingPuts: Array<() => void> = []

const pumpDirectPuts = () => {
  while (
    directUploadInFlight < DIRECT_UPLOAD_CONCURRENT &&
    pendingPuts.length > 0
  ) {
    const start = pendingPuts.shift()
    if (!start) break
    directUploadInFlight += 1
    start()
  }
  tickUploads()
}

export const tickUploads = () => {
  if (ticking) return
  ticking = true
  try {
    while (serverInFlight < MAX_UPLOAD_CONCURRENT) {
      const job = useCptUploadStore
        .getState()
        .claimNextQueued((item) => !item.directGcs)
      if (!job) break
      serverInFlight += 1
      void runJob(job).finally(() => {
        serverInFlight -= 1
        tickUploads()
      })
    }
    while (
      encodeInFlight < DIRECT_ENCODE_CONCURRENT &&
      pendingPuts.length < MAX_ENCODED_WAITING
    ) {
      const job = useCptUploadStore
        .getState()
        .claimNextQueued((item) => Boolean(item.directGcs))
      if (!job) break
      encodeInFlight += 1
      void runDirectEncode(job).finally(() => {
        encodeInFlight -= 1
        tickUploads()
      })
    }
  } finally {
    ticking = false
  }
}

const tickProgress = (id: string, from: number, to: number) => {
  const { patchJob } = useCptUploadStore.getState()
  let current = from
  patchJob(id, { progress: current })
  const timer = window.setInterval(() => {
    current = Math.min(current + 1, to)
    patchJob(id, { progress: current })
    if (current >= to) window.clearInterval(timer)
  }, 80)
  return () => window.clearInterval(timer)
}

const putGcs = async (url: string, blob: Blob) => {
  let res: Response
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/webp' },
      body: blob,
    })
  } catch {
    throw new Error('直传 GCS 失败，多半是存储桶没有放开浏览器上传')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text.slice(0, 180) || `GCS 上传失败 ${res.status}`)
  }
}

type DirectSign = {
  id: string
  originalId: string
  webp512Id: string
  coverId?: string
  urls: {
    component: string
    original: string
    webp512: string
    cover?: string
  }
}

type DirectReady = {
  signed: DirectSign
  crop: { x: number; y: number; w: number; h: number }
}

const failDirectJob = (job: CptUploadJob, error: unknown) => {
  useCptUploadStore.getState().patchJob(job.id, {
    status: 'error',
    progress: 0,
    error: error instanceof Error ? error.message : String(error),
  })
}

/** 只负责出图。画布用完就让出名额，避免多张 2048 图同时留在内存里。 */
const runDirectEncode = async (job: CptUploadJob): Promise<void> => {
  const { patchJob } = useCptUploadStore.getState()
  try {
    if (!job.layerPlace || !job.baseSize) {
      throw new Error('缺少图层坐标或画布尺寸')
    }
    const canvas = job.layerCanvas ?? job.decodeLayer?.()
    if (!canvas) {
      throw new Error('图层像素已不在，请重新导入这一层')
    }
    const releaseAfter = !job.layerCanvas
    try {
      patchJob(job.id, { status: 'compressing', progress: 8 })
      if (!job.preview) {
        const preview = previewFromCanvas(canvas)
        if (preview) patchJob(job.id, { preview })
      }
      const assets = await preparePsdAssets({
        image: canvas,
        left: job.layerPlace.left,
        top: job.layerPlace.top,
        canvasSize: job.layerPlace.canvas,
        canvasHeight: job.layerPlace.canvasHeight,
        baseSize: job.baseSize,
        baseHeight: job.baseHeight,
        withCover: (job.autoCover ?? 0) > 0,
        autoCover: job.autoCover ?? 0,
        clip: job.layerPlace.clip,
      })
      patchJob(job.id, { status: 'uploading', progress: 28 })
      scheduleDirectPut(job, assets)
    } finally {
      if (releaseAfter) releaseCanvas(canvas)
    }
  } catch (error) {
    failDirectJob(job, error)
  }
}

const scheduleDirectPut = (
  job: CptUploadJob,
  assets: Awaited<ReturnType<typeof preparePsdAssets>>,
) => {
  pendingPuts.push(() => {
    void runDirectPut(job, assets).finally(() => {
      directUploadInFlight -= 1
      pumpDirectPuts()
    })
  })
  pumpDirectPuts()
}

const runDirectPut = async (
  job: CptUploadJob,
  assets: Awaited<ReturnType<typeof preparePsdAssets>>,
) => {
  const { patchJob } = useCptUploadStore.getState()
  try {
    const signed = (await postFetcher('/components/direct/sign', {
      arg: { withCover: Boolean(assets.cover) },
    })) as DirectSign
    const uploads: Array<[string, Blob]> = [
      [signed.urls.component, assets.component],
      [signed.urls.original, assets.original],
      [signed.urls.webp512, assets.square],
    ]
    if (assets.cover) {
      if (!signed.urls.cover) throw new Error('缺少封面上传地址')
      uploads.push([signed.urls.cover, assets.cover])
    }
    let done = 0
    await Promise.all(
      uploads.map(async ([url, blob]) => {
        await putGcs(url, blob)
        done += 1
        patchJob(job.id, {
          status: 'uploading',
          progress: 28 + Math.round((done / uploads.length) * 55),
        })
      }),
    )
    void commitDirectComponent(job, { signed, crop: assets.crop })
  } catch (error) {
    failDirectJob(job, error)
  }
}

const listInserts = new Map<string, Promise<unknown>>()

/** 并行创建时按模板排队插入，避免两次 mutate 读到同一份列表互相盖掉。 */
const insertCreated = (tid: string, created: unknown) => {
  const run = () => optimisticAddComponents(tid, [created])
  const prev = listInserts.get(tid) ?? Promise.resolve()
  const next = prev.then(run, run)
  listInserts.set(tid, next)
  return next
}

const commitDirectComponent = async (job: CptUploadJob, ready: DirectReady) => {
  const { patchJob, completeJob } = useCptUploadStore.getState()
  const { signed, crop } = ready
  try {
    patchJob(job.id, { status: 'processing', progress: 90 })
    const result = await postFetcher('/components/direct/commit', {
      arg: {
        id: signed.id,
        originalId: signed.originalId,
        webp512Id: signed.webp512Id,
        coverId: signed.coverId,
        tid: job.tid,
        lid: job.lid,
        dlc: job.dlc,
        fileName: job.fileName,
        ai: job.ai,
        crop,
      },
    })
    const created = result?.data
    if (created) {
      await insertCreated(job.tid, created)
    } else {
      await mutate(['/components', { tid: job.tid }])
    }
    toast({
      title: '组件已就绪',
      description: job.fileName || created?.id,
    })
    completeJob(job.id)
  } catch (error) {
    patchJob(job.id, {
      status: 'error',
      progress: 0,
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

const runJob = async (job: CptUploadJob) => {
  const { patchJob, completeJob } = useCptUploadStore.getState()
  let stopTick: (() => void) | undefined
  try {
    let padded = job.original
    if (job.canvasPad) {
      patchJob(job.id, { status: 'padding', progress: 8 })
      stopTick = tickProgress(job.id, 8, 16)
      padded = await padImageDataUrl(job.original, job.canvasPad)
      stopTick()
    }
    patchJob(job.id, { status: 'compressing', progress: 16 })
    stopTick = tickProgress(job.id, 16, 22)
    const original = await ensureWebpDataUrl(padded)
    const blob = await dataUrlToBlob(original)
    stopTick()
    stopTick = undefined
    patchJob(job.id, { status: 'uploading', progress: 22 })

    const formData = new FormData()
    formData.append('original', blob, job.fileName || 'original.webp')
    formData.append('tid', job.tid)
    formData.append('lid', job.lid)
    formData.append('trim', String(job.trim))
    formData.append('ai', String(Boolean(job.ai)))
    formData.append('dlc', job.dlc ?? '')
    formData.append('fileName', job.fileName || '')
    formData.append('autoCover', String(job.autoCover ?? 0))
    if (job.layerPlace) {
      formData.append('placeLeft', String(job.layerPlace.left))
      formData.append('placeTop', String(job.layerPlace.top))
      formData.append('placeCanvas', String(job.layerPlace.canvas))
    }

    let lastShown = 22
    const result = await postFormWithProgress('/components', formData, {
      timeout: 180000,
      onProgress: (percent) => {
        const mapped = 22 + Math.round(percent * 0.63)
        if (mapped >= lastShown + 3 || percent >= 100) {
          lastShown = mapped
          patchJob(job.id, { status: 'uploading', progress: mapped })
        }
      },
      onUploaded: () =>
        patchJob(job.id, { status: 'processing', progress: 90 }),
    })

    const created = result?.data
    if (created) {
      await optimisticAddComponents(job.tid, [created])
    } else {
      await mutate(['/components', { tid: job.tid }])
    }
    toast({
      title: '组件已就绪',
      description: job.fileName || created?.id,
    })
    completeJob(job.id)
  } catch (error) {
    patchJob(job.id, {
      status: 'error',
      progress: 0,
      error: error instanceof Error ? error.message : String(error),
    })
  } finally {
    stopTick?.()
  }
}

export const enqueueCptUploads = async (items: EnqueueCptUpload[]) => {
  if (!items.length) return
  await bootCptUploads()
  await useCptUploadStore.getState().enqueue(items)
}
